import React, { useEffect, useState } from 'react';

import { Checkbox, ControlLabel, FormGroup, HelpBlock, Radio } from '@shared/components/bootstrap';

import { SelectFormControl } from '@admin/components/fields';
import { Ticket__Priority } from '@admin/schema';

export enum BusinessUnit {
  Moving = 'Moving',
  SmartStorage = 'Smart Storage',
  SelfStorage = 'Self Storage',
  Enterprise = 'Enterprise',
  Marketplace = 'Marketplace',
  ClutterInternal = 'Clutter Internal',
}

export enum Area {
  Billing = 'Billing',
  Field = 'Field',
  Warehouse = 'Warehouse',
  Funnel = 'Funnel',
  AccountPortal = 'Account Portal',
  AdminTeal = 'Admin Teal',
  Dispatch = 'Dispatch',
  UserManagement = 'User Management',
  CustomerCommunications = 'Customer Communications',
  Coupons = 'Coupons',
  Shipping = 'Shipping',
}

const businessUnitOptions = Object.values(BusinessUnit);
const areaOptions = Object.values(Area);

interface ICreateTicketProps {
  priority?: Ticket__Priority;
  description?: string;
  explanation?: string;
  notify: boolean;
  error?: Error;
  businessUnit?: string;
  area?: string;
  onPriority(value: Ticket__Priority): void;
  onDescription(value: string): void;
  onNotify(value: boolean): void;
  onExplanation(value: string): void;
  onBusinessUnit(value: BusinessUnit): void;
  onArea(value: Area): void;
}

import { getPriorityFormOptions } from '@admin/utils/ticket_priorities';

const PRIORITIES = getPriorityFormOptions();

const REQUEST_PLACEHOLDER =
  'Please describe what needs to fixed. Please include \
if applicable: account, order, items, pallets, or employee name \
that is experiencing issues.';

const EXPLANATION_PLACEHOLDER =
  'Please provide context for the request. Did the product not work as expected? \
Was there a process breakdown? Is a customer being affected by this issue?';

const EXTREME_PRIORITY_WARNING =
  'I understand this will immediately call an on duty engineer. \
If not extremely urgent, please use High Priority.';

export const TechSupportForm: React.FC<ICreateTicketProps> = ({
  area,
  businessUnit,
  description,
  error,
  explanation,
  notify,
  priority,
  onArea,
  onBusinessUnit,
  onDescription,
  onExplanation,
  onNotify,
  onPriority,
}) => {
  const [validationState, setValidationState] = useState<'error' | undefined>();

  useEffect(() => setValidationState(error ? 'error' : undefined), [error]);

  const isExtremePriority = priority === Ticket__Priority.Extreme;

  return (
    <React.Fragment>
      <ControlLabel>
        <span>Select Priority: </span>
      </ControlLabel>
      <FormGroup has={validationState}>
        {PRIORITIES.map((priorityOption) => (
          <Radio
            key={priorityOption.value}
            has={priorityOption.value === Ticket__Priority.Extreme ? 'error' : undefined}
          >
            <input
              type="radio"
              required
              name="priority"
              checked={priority === priorityOption.value}
              onChange={() => onPriority(priorityOption.value)}
            />
            {priorityOption.name} - {priorityOption.details}
          </Radio>
        ))}
      </FormGroup>
      <FormGroup has={validationState}>
        <ControlLabel>Request:</ControlLabel>
        <textarea
          required
          placeholder={REQUEST_PLACEHOLDER}
          style={{ resize: 'vertical' }}
          className="form-control"
          onChange={(event) => onDescription(event.target.value)}
          value={description}
        />
      </FormGroup>
      <FormGroup has={validationState}>
        <ControlLabel>Context:</ControlLabel>
        <textarea
          required
          placeholder={EXPLANATION_PLACEHOLDER}
          style={{ resize: 'vertical' }}
          className="form-control"
          onChange={(event) => onExplanation(event.target.value)}
          value={explanation}
        />
      </FormGroup>
      <FormGroup has={validationState}>
        <ControlLabel>Business Unit:</ControlLabel>
        <SelectFormControl
          options={businessUnitOptions}
          onChange={(event) => onBusinessUnit(event as BusinessUnit)}
          value={businessUnit}
          prompt="Select a Business Unit"
          required={true}
        />
      </FormGroup>
      <FormGroup has={validationState}>
        <ControlLabel>Area:</ControlLabel>
        <SelectFormControl
          options={areaOptions}
          onChange={(event) => onArea(event as Area)}
          value={area}
          prompt="Select an Area"
          required={true}
        />
      </FormGroup>
      <FormGroup>
        <ControlLabel>
          <input type="checkbox" onChange={(event) => onNotify(event.target.checked)} checked={notify} /> Notify
          requestor and assignee on updates
        </ControlLabel>
      </FormGroup>
      <FormGroup>
        {isExtremePriority && (
          <Checkbox has="error">
            <input type="checkbox" required />
            {EXTREME_PRIORITY_WARNING}
          </Checkbox>
        )}
      </FormGroup>
      {error && error.message && <HelpBlock className="text-danger">{error.message}</HelpBlock>}
    </React.Fragment>
  );
};
